/**
 * workspaceData.js
 *
 * Pure data layer of the workspace dashboard: which requests a board needs, and
 * how a widget reads its numbers out of the `GET /api/main-dashboard/summary`
 * payload. React-free and IO-free (the fetching lives in `useWorkspaceData`).
 *
 * Summary contract (backend `mainDashboard.service.js`):
 * ```
 * { range, generatedAt,
 *   modules: { <key>: { status: 'ok'|'forbidden'|'error'|'timeout', data: { kpis, series, breakdowns, lists, actions } } },
 *   actions: { counts, items } }
 * ```
 * `kpis[k] = {value, previous, unit, currency}`, `series[k] = {unit, currency, points[{t,v}], compare[{t,v}]}`,
 * `breakdowns[k] = {unit, currency, items[{key,label,value,tone}]}`, `lists[k] = {total, items[]}`.
 *
 * @module helpers/workspaces/workspaceData
 */

import {resolveWidgetDefinition} from '../../config/workspaceCatalog';
import {DEFAULT_PERIOD} from './workspaceModel';
import {LOCALE_TAGS, resolveLabelLanguage} from './workspaceLabels';

const isNum = (value) => typeof value === 'number' && Number.isFinite(value);

/** Widget render states. */
export const WIDGET_STATUS = Object.freeze({
    OK: 'ok',
    EMPTY: 'empty',
    FORBIDDEN: 'forbidden',
    ERROR: 'error',
    UNSUPPORTED: 'unsupported',
});

/**
 * Module keys a definition reads from. `null` entries (cross-module widgets) mean "all modules".
 *
 * @param {object} definition Catalogue definition.
 * @returns {Array<?string>} Module keys.
 */
export const modulesOf = (definition) => {
    if (!definition || definition.kind === 'unsupported') {
        return [];
    }

    if (definition.kind === 'list' && Array.isArray(definition.lists)) {
        return Array.from(new Set(definition.lists.map((source) => source.module)));
    }

    return [definition.module || null];
};

/**
 * Stable key of one summary request.
 *
 * @param {{period: string, restaurantId: ?string}} request Request descriptor.
 * @returns {string} Key.
 */
export const requestKey = ({period, restaurantId}) => `${restaurantId || '-'}|${period || DEFAULT_PERIOD}`;

/**
 * Group a board's widgets into the minimal set of summary requests: one per
 * distinct `(store, period)` pair, each asking only for the modules its widgets
 * need (all modules when a cross-module widget is in the group).
 *
 * @param {Array<object>} widgets Widget instances.
 * @param {{period?: string, restaurantId?: ?string}} [defaults] Board period and default store.
 * @returns {Array<{key: string, period: string, restaurantId: ?string, modules: ?string[]}>} Requests.
 */
export const planRequests = (widgets, {period = DEFAULT_PERIOD, restaurantId = null} = {}) => {
    const groups = new Map();

    (Array.isArray(widgets) ? widgets : []).forEach((widget) => {
        const definition = resolveWidgetDefinition(widget.type);
        const needs = modulesOf(definition);

        if (!needs.length) {
            return;
        }

        const request = {
            period: widget.options?.period || period,
            restaurantId: widget.options?.restaurantId || restaurantId || null,
        };
        const key = requestKey(request);
        const group = groups.get(key) || {key, ...request, modules: new Set(), all: false};

        needs.forEach((module) => {
            if (module) {
                group.modules.add(module);
            } else {
                group.all = true;
            }
        });
        groups.set(key, group);
    });

    return Array.from(groups.values()).map(({modules, all, ...rest}) => ({
        ...rest,
        modules: all ? null : Array.from(modules).sort(),
    }));
};

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

/**
 * @param {?object} summary Summary payload.
 * @param {?string} module Module key.
 * @returns {{status: string, data: object}} Module result (`missing` when absent).
 */
export const moduleResult = (summary, module) => {
    const result = module ? summary?.modules?.[module] : null;

    return result ? {status: result.status, data: result.data || {}} : {status: 'missing', data: {}};
};

/**
 * @param {?object} summary Summary payload.
 * @param {string} module Module key.
 * @param {string} key KPI key.
 * @returns {?{value: ?number, previous: ?number, unit: ?string, currency: ?string}} The KPI.
 */
export const readKpi = (summary, module, key) => moduleResult(summary, module).data.kpis?.[key] || null;

/**
 * @param {?object} summary Summary payload.
 * @param {string} module Module key.
 * @param {string} key Series key.
 * @returns {?{unit: ?string, currency: ?string, points: Array, compare: Array}} The series.
 */
export const readSeries = (summary, module, key) => moduleResult(summary, module).data.series?.[key] || null;

/**
 * @param {?object} summary Summary payload.
 * @param {string} module Module key.
 * @param {string} key Breakdown key.
 * @returns {?{unit: ?string, currency: ?string, items: Array}} The breakdown.
 */
export const readBreakdown = (summary, module, key) => moduleResult(summary, module).data.breakdowns?.[key] || null;

/**
 * Merge the source lists of a list widget (a timeline reads several) in order.
 *
 * @param {?object} summary Summary payload.
 * @param {Array<{module: string, list: string}>} sources List sources.
 * @param {number} [limit] Row cap.
 * @returns {{total: ?number, items: Array}} Items and the server total (`null` when sources disagree on it).
 */
export const readLists = (summary, sources, limit) => {
    const items = [];
    let total = 0;
    let known = true;

    (Array.isArray(sources) ? sources : []).forEach((source) => {
        const list = moduleResult(summary, source.module).data.lists?.[source.list];

        if (!list) {
            return;
        }

        items.push(...(Array.isArray(list.items) ? list.items : []));
        if (isNum(list.total)) {
            total += list.total;
        } else {
            known = false;
        }
    });

    return {total: known ? total : null, items: limit ? items.slice(0, limit) : items};
};

/**
 * Actions for an actions widget: the merged, priority-sorted feed, optionally
 * narrowed to one module and/or one priority.
 *
 * @param {?object} summary Summary payload.
 * @param {{module?: ?string, priority?: ?string, limit?: number}} [filter] Filter.
 * @returns {{items: Array, counts: object}} Actions.
 */
export const readActions = (summary, {module = null, priority = null, limit = 6} = {}) => {
    const all = Array.isArray(summary?.actions?.items) ? summary.actions.items : [];
    const items = all
        .filter((action) => (!module || action.moduleKey === module) && (!priority || priority === 'all' || action.priority === priority))
        .slice(0, limit);

    return {items, counts: summary?.actions?.counts || {}};
};

/**
 * Percentage change of a KPI against the previous period.
 *
 * @param {?object} kpi KPI node.
 * @param {boolean} [invert] True when a rise is bad (failed runs, overdue) - flips the sign's meaning only via `good`.
 * @returns {?{pct: number, up: boolean, good: boolean}} Delta, `null` when it cannot be computed.
 */
export const kpiDelta = (kpi, invert = false) => {
    if (!kpi || !isNum(kpi.value) || !isNum(kpi.previous)) {
        return null;
    }

    if (kpi.previous === 0) {
        return kpi.value === 0 ? null : {pct: 100, up: kpi.value > 0, good: invert ? kpi.value < 0 : kpi.value > 0};
    }

    const pct = ((kpi.value - kpi.previous) / Math.abs(kpi.previous)) * 100;

    if (Math.abs(pct) < 0.05) {
        return null;
    }

    return {pct: Math.round(pct * 10) / 10, up: pct > 0, good: invert ? pct < 0 : pct > 0};
};

/**
 * Numeric points of a series, ready for a chart.
 *
 * @param {?object} series Series node.
 * @returns {number[]} Values (non-finite points dropped).
 */
export const seriesValues = (series) => (Array.isArray(series?.points) ? series.points : [])
    .map((point) => (isNum(point?.v) ? point.v : (isNum(point?.value) ? point.value : null)))
    .filter((value) => value !== null);

/**
 * Items of a breakdown: positive values only, largest first, capped; each with its share.
 *
 * @param {?object} breakdown Breakdown node.
 * @param {number} [limit] Row cap.
 * @returns {Array<{key: string, label: string, value: number, tone: ?string, share: number}>} Rows.
 */
export const breakdownRows = (breakdown, limit = 6) => {
    const items = (Array.isArray(breakdown?.items) ? breakdown.items : []).filter((item) => isNum(item?.value) && item.value > 0);
    const total = items.reduce((sum, item) => sum + item.value, 0) || 1;

    return items
        .slice()
        .sort((a, b) => b.value - a.value)
        .slice(0, limit)
        .map((item) => ({
            key: String(item.key ?? item.label),
            label: String(item.label ?? item.key ?? ''),
            value: item.value,
            tone: item.tone || null,
            share: item.value / total,
        }));
};

/**
 * Resolve what a widget should render for a given summary.
 *
 * @param {object} definition Catalogue definition (or the `unsupported` placeholder).
 * @param {?object} summary Summary payload (`null` before the first load).
 * @param {{limit?: number}} [options] Instance options.
 * @returns {{status: string, view: ?object}} State and the kind-specific view model.
 */
export const resolveWidgetView = (definition, summary, options = {}) => {
    if (!definition || definition.kind === 'unsupported') {
        return {status: WIDGET_STATUS.UNSUPPORTED, view: null};
    }

    const limit = options.limit || definition.limit || 6;
    const primary = definition.kind === 'list' ? definition.lists?.[0]?.module : definition.module;

    if (primary) {
        const {status} = moduleResult(summary, primary);

        if (status === 'forbidden') {
            return {status: WIDGET_STATUS.FORBIDDEN, view: null};
        }

        if (status === 'error' || status === 'timeout' || status === 'missing') {
            return {status: WIDGET_STATUS.ERROR, view: null};
        }
    }

    switch (definition.kind) {
        case 'metric': {
            const kpi = readKpi(summary, definition.module, definition.kpi);
            const series = definition.series ? readSeries(summary, definition.module, definition.series) : null;

            return kpi && isNum(kpi.value)
                ? {status: WIDGET_STATUS.OK, view: {kpi, series, values: seriesValues(series), delta: kpiDelta(kpi, definition.invertDelta)}}
                : {status: WIDGET_STATUS.EMPTY, view: null};
        }

        case 'grid': {
            const tiles = (definition.kpis || [])
                .map((entry) => ({key: entry.kpi, kpi: readKpi(summary, definition.module, entry.kpi), invert: !!entry.invertDelta}))
                .filter((tile) => tile.kpi && isNum(tile.kpi.value))
                .map((tile) => ({...tile, delta: kpiDelta(tile.kpi, tile.invert)}));

            return tiles.length ? {status: WIDGET_STATUS.OK, view: {tiles}} : {status: WIDGET_STATUS.EMPTY, view: null};
        }

        case 'trend': {
            const series = readSeries(summary, definition.module, definition.series);
            const values = seriesValues(series);

            return values.length
                ? {status: WIDGET_STATUS.OK, view: {series, values, compare: seriesValues({points: series.compare}), total: values.reduce((a, b) => a + b, 0)}}
                : {status: WIDGET_STATUS.EMPTY, view: null};
        }

        case 'breakdown': {
            const breakdown = readBreakdown(summary, definition.module, definition.breakdown);
            const rows = breakdownRows(breakdown, limit);

            return rows.length ? {status: WIDGET_STATUS.OK, view: {breakdown, rows}} : {status: WIDGET_STATUS.EMPTY, view: null};
        }

        case 'list': {
            const {items, total} = readLists(summary, definition.lists, limit);

            return items.length ? {status: WIDGET_STATUS.OK, view: {items, total}} : {status: WIDGET_STATUS.EMPTY, view: null};
        }

        case 'actions': {
            const actions = readActions(summary, {module: definition.module, priority: definition.priority, limit});

            return {status: actions.items.length ? WIDGET_STATUS.OK : WIDGET_STATUS.EMPTY, view: actions};
        }

        default:
            return {status: WIDGET_STATUS.UNSUPPORTED, view: null};
    }
};

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

const intl = (language, options, value) => {
    try {
        return new globalThis.Intl.NumberFormat(LOCALE_TAGS[resolveLabelLanguage(language)], options).format(value);
    } catch (_error) {
        return String(Math.round(value * 100) / 100);
    }
};

/**
 * Compact count (1 200 -> 1.2K style via Intl).
 *
 * @param {number} value Number.
 * @param {?string} language i18n language.
 * @returns {string} Text (`-` for a non-number).
 */
export const formatCount = (value, language) => (isNum(value)
    ? intl(language, {notation: Math.abs(value) >= 100000 ? 'compact' : 'standard', maximumFractionDigits: 1}, value)
    : '-');

/**
 * Money with the currency, compacting large amounts (1 250 000 Ft -> 1,3 M Ft).
 *
 * @param {number} value Amount.
 * @param {?string} currency ISO currency (HUF when absent).
 * @param {?string} language i18n language.
 * @returns {string} Text.
 */
export const formatMoney = (value, currency, language) => {
    if (!isNum(value)) {
        return '-';
    }

    const code = currency || 'HUF';
    const noDecimals = code === 'HUF' || Math.abs(value) >= 1000;

    return intl(language, {
        style: 'currency',
        currency: code,
        notation: Math.abs(value) >= 1000000 ? 'compact' : 'standard',
        maximumFractionDigits: noDecimals ? 0 : 2,
    }, value);
};

/**
 * Format a KPI according to its unit.
 *
 * @param {?object} kpi KPI node.
 * @param {?string} language i18n language.
 * @returns {string} Text.
 */
export const formatKpi = (kpi, language) => {
    if (!kpi || !isNum(kpi.value)) {
        return '-';
    }

    switch (kpi.unit) {
        case 'currency':
            return formatMoney(kpi.value, kpi.currency, language);
        case 'percent':
            return `${intl(language, {maximumFractionDigits: 1}, kpi.value)}%`;
        case 'hours':
            return `${intl(language, {maximumFractionDigits: 1}, kpi.value)} h`;
        case 'days':
            return `${intl(language, {maximumFractionDigits: 1}, kpi.value)} d`;
        default:
            return formatCount(kpi.value, language);
    }
};

/**
 * Format a plain value of a series / breakdown with that node's unit.
 *
 * @param {number} value Number.
 * @param {?{unit: ?string, currency: ?string}} node Series or breakdown node.
 * @param {?string} language i18n language.
 * @returns {string} Text.
 */
export const formatUnitValue = (value, node, language) => formatKpi({value, unit: node?.unit, currency: node?.currency}, language);

/**
 * Short date for list rows.
 *
 * @param {?string} value ISO date.
 * @param {?string} language i18n language.
 * @returns {string} Text (empty for an invalid date).
 */
export const formatShortDate = (value, language) => {
    const date = value ? new Date(value) : null;

    if (!date || Number.isNaN(date.getTime())) {
        return '';
    }

    try {
        return date.toLocaleDateString(LOCALE_TAGS[resolveLabelLanguage(language)], {month: 'short', day: 'numeric'});
    } catch (_error) {
        return date.toISOString().slice(5, 10);
    }
};

/**
 * Stores a user can pick for a widget. Tolerates every shape `user.restaurants` has taken.
 *
 * @param {?object} user Signed-in user.
 * @returns {Array<{id: string, name: string}>} Stores (deduped).
 */
export const listUserStores = (user) => {
    const seen = new Set();
    const stores = [];

    (Array.isArray(user?.restaurants) ? user.restaurants : []).forEach((item) => {
        const source = item?.restaurant && typeof item.restaurant === 'object' ? item.restaurant : item;
        const id = String(source?._id || source?.id || source?.restaurantId || item?.restaurantId || (typeof item === 'string' ? item : '') || '');
        const name = source?.name || source?.restaurantName || item?.name || '';

        if (id && !seen.has(id)) {
            seen.add(id);
            stores.push({id, name: String(name || id)});
        }
    });

    return stores;
};

/**
 * Whether the caller may use a catalogue widget, judged by the backend's own
 * module gate (`GET /api/main-dashboard/modules`). Cross-module widgets
 * (`module: null`) are always offered - the backend filters their rows per
 * module. While the gate is unknown (`accessMap` null: offline / first load) every
 * widget is offered and the card itself renders the "locked" state if the
 * backend then refuses it.
 *
 * @param {object} definition Catalogue definition.
 * @param {?Object.<string, boolean>} accessMap `moduleKey -> accessible`.
 * @returns {boolean} True when the widget may be added / rendered.
 */
export const isWidgetAllowed = (definition, accessMap) => {
    if (!definition || definition.kind === 'unsupported') {
        return false;
    }

    if (!accessMap) {
        return true;
    }

    return modulesOf(definition).every((module) => !module || accessMap[module] !== false);
};
